import birdlifeDataByScientificName from "../assets/data/birdlife-data-2026.json";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies} from "ebird-mydata-reader";

export type BirdLifeRedListCategory = 'EX'|'EW'|'CR'|'EN'|'VU'|'NT'|'LC'|'DD';

export type BirdLifeSpeciesData = {
    sisId?: string;
    sequence?: string;
    family?: string;
    scientificName: string;
    commonName?: string;
    redListCategory?: BirdLifeRedListCategory|string;
    possiblyExtinct?: boolean;
    possiblyExtinctInTheWild?: boolean;
    seabird?: boolean;
    waterbird?: boolean;
    landbird?: boolean;
    migratoryStatus?: string;
    terrestrial?: boolean;
    freshwater?: boolean;
    marine?: boolean;
    areaOfOccupancy?: string;
    criteria?: string;
    extentOfOccurrence?: string;
    populationSize?: string;
    populationSizeDerivation?: string;
    populationTrend?: string;
    populationTrendDerivation?: string;
}

export type BirdLifeFamilySummary = {
    matchedSpecies: number;
    threatenedSpecies: number;
    extinctSpecies: number;
    decreasingSpecies: number;
    categories: Record<string, number>;
}

const dataByScientificName = birdlifeDataByScientificName as Record<string, BirdLifeSpeciesData>;
const threatenedCategories = new Set(['CR', 'EN', 'VU']);
const extinctCategories = new Set(['EX', 'EW']);

export const redListCategoryLabels: Record<string, string> = {
    EX: 'Extinct',
    EW: 'Extinct in the Wild',
    CR: 'Critically Endangered',
    EN: 'Endangered',
    VU: 'Vulnerable',
    NT: 'Near Threatened',
    LC: 'Least Concern',
    DD: 'Data Deficient'
};

export const getBirdLifeSpeciesData = (species?: EBirdChecklistBySpecies | null) => {
    if (!species) return undefined;

    return dataByScientificName[species.scientificName];
}

export const getRedListCategoryLabel = (category?: string) => {
    if (!category) return 'Not Available';

    return redListCategoryLabels[category] ?? category;
}

export const isThreatenedRedListCategory = (category?: string) => {
    return threatenedCategories.has(category ?? '');
}

export const isExtinctRedListCategory = (category?: string) => {
    return extinctCategories.has(category ?? '');
}

export const getBirdLifeFamilySummary = (family?: EBirdChecklistByFamily | null): BirdLifeFamilySummary => {
    const summary: BirdLifeFamilySummary = {
        matchedSpecies: 0,
        threatenedSpecies: 0,
        extinctSpecies: 0,
        decreasingSpecies: 0,
        categories: {}
    };

    if (!family) return summary;

    for (const species of family.speciesList) {
        const birdLifeSpecies = getBirdLifeSpeciesData(species);
        if (!birdLifeSpecies) continue;

        summary.matchedSpecies++;
        const category = birdLifeSpecies.redListCategory ?? 'Unknown';
        summary.categories[category] = (summary.categories[category] ?? 0) + 1;

        if (isThreatenedRedListCategory(category)) {
            summary.threatenedSpecies++;
        }

        if (isExtinctRedListCategory(category)) {
            summary.extinctSpecies++;
        }

        if (birdLifeSpecies.populationTrend === 'Decreasing') {
            summary.decreasingSpecies++;
        }
    }

    return summary;
}

export const formatBirdLifeList = (values: Array<string | undefined | false>) => {
    return values.filter(Boolean).join(', ') || 'Not Available';
}

export const formatBirdLifePopulationSize = (populationSize?: string) => {
    if (!populationSize) return 'Not Available';

    return populationSize.replace(/\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?/g, value => {
        return Number(value.replace(/,/g, '')).toLocaleString();
    });
}
